import { FC, useMemo } from "react";
import BottomKPIBar, {
  KPI
} from "@/components/controls/BottomKPIBar/BottomKPIBar";
import { ContractsAggregations } from "@/pages/contracts/hooks";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { deltaHeader } from "@/entities/dataTables/compareTo/columnIds";

interface ContractsKPIData extends Record<string, number> {
  total: number;
  tcv: number;
  acv: number;
  arr: number;
  mrr: number;
  avgArr: number;
  avgMrr: number;
  carr: number;
  cmrr: number;
  arrDelta: number;
  mrrDelta: number;
}

const BASE_KPIS: KPI<ContractsKPIData>[] = [
  { name: "total" },
  { name: "tcv", isMonetary: true },
  { name: "acv", isMonetary: true }
];

const ARR_KPIS: KPI<ContractsKPIData>[] = [
  { name: "arr", isMonetary: true },
  { name: "avgArr", isMonetary: true },
  { name: "carr", isMonetary: true }
];

const MRR_KPIS: KPI<ContractsKPIData>[] = [
  { name: "mrr", isMonetary: true },
  { name: "avgMrr", isMonetary: true },
  { name: "cmrr", isMonetary: true }
];

interface Props {
  aggregations: ContractsAggregations;
  isLoading?: boolean;
  /**
   * The selected Compare-to timeframe as the Δ column headers word it, or
   * undefined when Compare to is off. Passed in already formatted so the bar
   * and the columns cannot label the same period two different ways — the
   * table composes it once from `formatTimeframeLabel`.
   */
  compareTimeframeLabel?: string;
  /**
   * The metric's own name as the Δ column headers spell it, e.g. `ARR`.
   *
   * Passed down for the same reason the timeframe is, and it is the half that
   * is easy to get wrong: the bar has an ARR/MRR label of its own on the
   * `useArrMrr` context, sourced from `terms.arr`, while the columns are
   * headed from `contracts.arr`. Both read "ARR" today, so letting the bar use
   * its own would satisfy the acceptance criterion by coincidence and break it
   * the first time either key is reworded on its own. Taking the table's
   * string makes the two identical by construction.
   */
  compareMetricLabel?: string;
  isError?: boolean;
}

const ContractsBottomKPIBar: FC<Props> = ({
  aggregations,
  isLoading,
  compareTimeframeLabel,
  compareMetricLabel,
  isError
}) => {
  // Gate on isLoading: isResolved never turns true on a failed read.
  const { isArr, isLoading: arrMrrLoading } = useArrMrr();

  // How much the whole filtered book moved over the period: today's total less
  // the same set's total at the period start, both served by the same read so
  // they cover the same records.
  //
  // Null — not zero — whenever the movement is unknown: no period selected, or
  // no contract in the filter had a figure that far back. Rendering the second
  // as a rise of the entire current total would report a book that was there
  // all along as brand new, which is the same distinction the Δ cells draw by
  // leaving themselves blank.
  //
  // Each basis is computed from its own pair rather than the active one being
  // computed and copied into both keys: the record below declares `arrDelta`
  // and `mrrDelta` as independent figures, like every other pair on it, and a
  // key holding the *other* metric's movement would be wrong the moment
  // anything read it directly.
  const deltaFor = (current: number, asOf: number | null): number | null =>
    compareTimeframeLabel && asOf !== null ? current - asOf : null;
  const arrDelta = deltaFor(aggregations.totalArr, aggregations.asOfArr);
  const mrrDelta = deltaFor(aggregations.totalMrr, aggregations.asOfMrr);
  const delta = isArr ? arrDelta : mrrDelta;

  // Active basis only, and no basis at all until the setting resolves.
  const kpis = useMemo(() => {
    if (arrMrrLoading) return BASE_KPIS;
    return [
      ...BASE_KPIS,
      ...(isArr ? ARR_KPIS : MRR_KPIS),
      // Last in the bar, per the design, and absent entirely when there is no
      // movement to report — including while the basis is still in flight,
      // where the tile would have to guess which total it is comparing.
      //
      // `isError` keeps it present on a failed aggregations read, where every
      // other tile renders an em-dash. Dropping it there would be the one case
      // its absence lies: "no movement to report" and "we could not find out"
      // look identical in a bar that simply has one fewer tile, and the second
      // is what the rest of the bar is busy saying.
      ...((delta !== null || isError) &&
      compareTimeframeLabel &&
      compareMetricLabel
        ? [
            {
              // Keyed by basis like every other recurring-revenue tile, so a
              // test hook or a screenshot names the figure it actually holds.
              name: isArr ? ("arrDelta" as const) : ("mrrDelta" as const),
              isMonetary: true,
              isDelta: true,
              label: deltaHeader(compareMetricLabel, compareTimeframeLabel)
            }
          ]
        : [])
    ];
  }, [
    isArr,
    arrMrrLoading,
    delta,
    compareTimeframeLabel,
    compareMetricLabel,
    isError
  ]);

  const data: ContractsKPIData = {
    total: aggregations.totalCount,
    tcv: aggregations.totalContractValue,
    acv: aggregations.totalAnnualContractValue,
    arr: aggregations.totalArr,
    mrr: aggregations.totalMrr,
    avgArr: aggregations.averageArr,
    avgMrr: aggregations.averageMrr,
    carr: aggregations.totalCarr,
    cmrr: aggregations.totalCmrr,
    arrDelta: arrDelta ?? 0,
    mrrDelta: mrrDelta ?? 0
  };

  return (
    <BottomKPIBar<ContractsKPIData>
      kpis={kpis}
      data={data}
      pageName="contracts"
      isLoading={isLoading}
      isError={isError}
    />
  );
};

export default ContractsBottomKPIBar;
